import DateFormComponent from "components/shared/FormComponent/DateFormComponent";
import StyledButton from "components/shared/StyledButton";
import { getCleanStartorEndDay } from "helpers/utils";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import styles from "./style.module.scss";

interface ICompletionDateModal {
    /**
     * Triggers closing of modal and submits data.
     */
    onSubmit: (completionDate: Date) => void;
    /**
     * Triggers closing of modal without submitting.
     */
    onClose: () => void;
    /**
     * Default completion date value
     */
    defaultDate?: Date | string | null;
    /**
     * Label for the date field. Defaults to the completion date label.
     */
    label?: string;
}

/**
 * Modal that asks which date to set when completion status changes to 'completed' (réalisé).
 * Also reused for payment date selection.
 */
const CompletionDateModal = ({ onSubmit, onClose, defaultDate, label }: ICompletionDateModal) => {
    const [t] = useTranslation();
    const [isLoading, setIsLoading] = useState(false);

    // Rebuild a local Date from the UTC calendar parts so the picker shows the day
    // the backend stored, not the previous day when the user sits west of UTC.
    const defaultValues = {
        completionDate: defaultDate ? getCleanStartorEndDay(defaultDate) : new Date(),
    };

    const {
        control,
        handleSubmit,
        formState: { errors },
    } = useForm({ defaultValues });

    const handleFormSubmit = (data: { completionDate: string | Date }) => {
        setIsLoading(true);
        const local = data.completionDate instanceof Date ? data.completionDate : new Date(data.completionDate);
        // Send UTC midnight of the local calendar day so the backend stores the
        // intended day regardless of the user's timezone.
        const utcDay = new Date(Date.UTC(local.getFullYear(), local.getMonth(), local.getDate()));
        onSubmit(utcDay);
        setIsLoading(false);
    };

    return (
        <form className={styles["completion-date-modal"]} onSubmit={handleSubmit(handleFormSubmit)}>
            <DateFormComponent
                control={control}
                controlName="completionDate"
                label={label ?? t("Articles.CompletionDate")}
                required
                error={errors.completionDate}
                maxYearDistance={10}
            />
            <div className={styles["buttons"]}>
                <StyledButton buttonType="outlined" onClick={onClose} type="button">
                    {t("Cancel")}
                </StyledButton>
                <StyledButton buttonType="primary" type="submit" loading={isLoading}>
                    {t("Validate")}
                </StyledButton>
            </div>
        </form>
    );
};

export default CompletionDateModal;
